import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
  Grid,
  Chip,
  InputAdornment,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addTransaction, addTransferencia } from '../slices/transactionSlice';
import { addIncome, addExpense } from '../slices/recurringSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { getCurrencyOptions, getCurrencySymbol, detectDefaultCurrency } from '../utils/currencyUtils';
import goldColors from '../themes/goldColors';
import apiClient from '../services/api';

// Iconos
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// Componentes estilizados
const FormPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '8px',
  padding: theme.spacing(0),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  }
}));

const FormHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const FormContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const FieldWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const CategoryChip = styled(Chip)(({ theme, selected, categoryColor }) => ({
  margin: theme.spacing(0.5),
  padding: theme.spacing(1.5, 1),
  height: 'auto',
  borderRadius: '6px',
  fontWeight: selected ? 600 : 400,
  backgroundColor: selected ? alpha(categoryColor || theme.palette.primary.main, 0.1) : theme.palette.background.paper,
  border: `1px solid ${selected ? (categoryColor || theme.palette.primary.main) : theme.palette.divider}`,
  color: selected ? (categoryColor || theme.palette.primary.main) : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? alpha(categoryColor || theme.palette.primary.main, 0.15) : alpha(theme.palette.action.hover, 0.1),
  },
  '& .MuiChip-icon': {
    color: 'inherit',
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
  '& .MuiChip-label': {
    padding: theme.spacing(0.5, 1),
  },
}));

const TypeButton = styled(Button)(({ theme, active, buttonType }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: '8px',
  fontWeight: 500,
  flex: 1,
  color: active ? 'white' : theme.palette.text.secondary,
  backgroundColor: active
    ? buttonType === 'income'
      ? theme.palette.success.main
      : buttonType === 'transfer'
        ? theme.palette.info.main
        : theme.palette.error.main
    : alpha(theme.palette.divider, 0.1),
  '&:hover': {
    backgroundColor: active
      ? buttonType === 'income'
        ? theme.palette.success.dark
        : buttonType === 'transfer'
          ? theme.palette.info.dark
          : theme.palette.error.dark
      : alpha(theme.palette.divider, 0.2),
  },
}));

// Esquema de validación que soporta español. 31 jul 2026: renombrado a los
// campos reales del backend (tipo/monto/categoria/descripcion) — antes
// usaba transaction_type/amount/category en inglés, que el schema
// TransactionCreate del backend no reconoce (tipo y monto son
// obligatorios ahí), así que este formulario siempre fallaba con 422.
const getValidationSchema = (language) => {
  const isSpanish = language === 'es';

  return Yup.object({
    monto: Yup.number()
      .required(isSpanish ? 'El monto es requerido' : 'Amount is required')
      .positive(isSpanish ? 'El monto debe ser positivo' : 'Amount must be positive'),
    descripcion: Yup.string()
      .max(100, isSpanish ? 'La descripción no debe exceder 100 caracteres' : 'Description must not exceed 100 characters'),
    categoria: Yup.string()
      .required(isSpanish ? 'La categoría es requerida' : 'Category is required'),
    tipo: Yup.string()
      .required(isSpanish ? 'El tipo de transacción es requerido' : 'Transaction type is required')
      .oneOf(['ingreso', 'gasto'], isSpanish ? 'Tipo de transacción inválido' : 'Invalid transaction type'),
    fecha: Yup.date()
      .required(isSpanish ? 'La fecha es requerida' : 'Date is required')
  });
};

// Mismas categorías (strings en español, usadas directamente como valor)
// que ya usa TransactionsPage.js — antes este formulario tenía su propia
// lista con valores en inglés ('food', 'transport'...) que se guardaban
// tal cual en `categoria`, quedando inconsistentes con el resto de la
// app (presupuestos, reportes, reglas de proveedor), que usan nombres en
// español como texto libre.
const CATEGORIAS = [
  'Salario', 'Alquiler', 'Alimentación', 'Transporte', 'Servicios',
  'Entretenimiento', 'Salud', 'Educación', 'Otro',
];

// Frecuencias ofrecidas al marcar la transacción como recurrente (31 jul
// 2026) — mismas 3 opciones que en TransactionsPage.js.
// 17 sep 2026: labels traducidos (mismo criterio que
// makeFrecuenciasRecurrente en TransactionsPage.js) -- el value que se
// guarda no cambia.
const makeFrecuenciasRecurrente = (isSpanish) => [
  { value: 'diario', label: isSpanish ? 'Diario' : 'Daily' },
  // 21 sep 2026 (Augusto: "no aparece frecuencia Semanal"): el backend
  // ya la proyectaba (_generate_occurrences en calendar.py) y
  // RecurringPage.js ya la ofrecía -- faltaba acá.
  { value: 'semanal', label: isSpanish ? 'Semanal' : 'Weekly' },
  { value: 'quincenal', label: isSpanish ? 'Días 15 y 30' : 'Days 15 and 30' },
  { value: 'mensual', label: isSpanish ? 'Mensual' : 'Monthly' },
];

// Mismo mapa de traducción de categorías fijas que CategoriaPicker en
// TransactionsPage.js -- el value guardado (CATEGORIAS de arriba) se
// mantiene en español siempre, solo cambia el label visible del chip.
const CATEGORIA_LABELS_EN = {
  Salario: 'Salary', Alquiler: 'Rent', Alimentación: 'Food & Dining', Transporte: 'Transport',
  Servicios: 'Utilities', Entretenimiento: 'Entertainment', Salud: 'Health', Educación: 'Education', Otro: 'Other',
};
const categoriaLabel = (cat, isSpanish) => (isSpanish ? cat : (CATEGORIA_LABELS_EN[cat] || cat));

const TransactionForm = ({ onClose, onBudgetAlert }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { language, t } = useLanguage();
  const isSpanish = language === 'es';
  const frecuenciasRecurrente = makeFrecuenciasRecurrente(isSpanish);
  const usuario = useSelector((state) => state.auth?.user);
  const { currency, changeCurrency, exchangeRates } = useCurrency();
  // 21 sep 2026 (Augusto: "siempre colocar USD y la moneda local de
  // donde es el usuario PRIMERO"). La moneda local sale del país del
  // usuario, igual que en CuentasPage.js -- no del toggle de moneda, que
  // es lo que está mirando ahora y puede cambiar mil veces al día.
  const monedaLocalUsuario = detectDefaultCurrency(usuario?.country);
  // 28 sep 2026: currencyOptions ya no se usa acá -- el selector de
  // visualización salió de este formulario (ver el comentario junto al
  // Grid del monto). Se deja monedaLocalUsuario porque sigue definiendo
  // el default del campo de moneda del movimiento.

  // Handle currency change
  const handleCurrencyChange = (event) => {
    changeCurrency(event.target.value);
  };

  // Estado para la fecha seleccionada
  const [selectedDate, setSelectedDate] = useState(new Date());

  // 3 sep 2026 (Augusto: "no existe la opcion para transferir de cuenta a
  // cuenta o a tarjetas, para mostrar pagos de tarjeta. ojo, los pagos de
  // tarjetas que aparecen en los estados bancarios vienen de las mismas
  // cuentas bancarias"): "Transferencia" vive como un tercer modo, aparte
  // de Ingreso/Egreso -- en vez de una categoría y (nada de cuenta, este
  // form nunca pidió cuenta), pide Cuenta de origen y Cuenta destino.
  // Una tarjeta de crédito ya ES una Account más (Account.tipo=
  // "tarjeta_credito", ver CuentasPage.js) -- así que "transferir a una
  // tarjeta" para pagarla es el mismo selector, no hace falta nada
  // especial para eso. modo se mantiene aparte de formik.values.tipo (que
  // solo acepta 'ingreso'/'gasto', ver getValidationSchema) para no tener
  // que meter 'transferencia' en ese enum y complicar la validación de
  // Yup del camino de siempre.
  const [modo, setModo] = useState('gasto'); // 'ingreso' | 'gasto' | 'transferencia'
  const [cuentas, setCuentas] = useState([]);
  // 4 sep 2026 (Augusto: "no dice que el dinero ingresó a BBVA Visa"): este
  // form (el diálogo global "Agregar Transacción" del header, ver
  // NotionHeader.js) nunca pidió Cuenta para un Ingreso/Gasto normal --
  // solo se agregó `cuentas` para Cuenta origen/destino de Transferencia
  // (ver comentario más abajo, junto al Select de Categoría). Por eso toda
  // transacción creada desde acá quedaba SIEMPRE sin cuenta asignada
  // (account_id null), sin ningún aviso -- ver formattedValues/
  // buildRecurringPayload más abajo.
  const [accountId, setAccountId] = useState('');
  const [cuentaOrigenId, setCuentaOrigenId] = useState('');
  const [cuentaDestinoId, setCuentaDestinoId] = useState('');
  // 21 sep 2026 (Augusto: "PCM no tiene una opción de transferencia de
  // cuenta USD a local"): cuánto LLEGA, en la moneda de la cuenta
  // destino. Solo se pide cuando las dos cuentas están en monedas
  // distintas -- ver _crear_transferencia_core en el backend. No se
  // estima con la tasa del día a propósito: el tipo de cambio que aplicó
  // el banco esa vez lo tiene solo el usuario, y estimarlo dejaría un
  // saldo que no calza con el del banco.
  const [montoDestino, setMontoDestino] = useState('');
  // 21 sep 2026 (Augusto: "sería bueno que existan las 2 opciones: 1)
  // colocar tipo de cambio o 2) adicionar monto"). Según el caso uno
  // tiene a la mano un dato u otro: el voucher del banco muestra el
  // monto depositado, pero una compra de dólares en una casa de cambio
  // se recuerda por la tasa.
  //
  // Lo que se le manda al backend es SIEMPRE el monto que llega. La tasa
  // es solo una forma de calcularlo acá: el saldo de la cuenta tiene que
  // salir del número real, no de una multiplicación que el backend
  // repita con otro redondeo.
  // 21 sep 2026 (Augusto: "solo cuando se paga de moneda local a usd o
  // viceversa se necesita el tipo de cambio... en este caso de pago de
  // deuda de dólares a una tarjeta de crédito que tiene deuda en dólares
  // y también deuda en moneda local").
  //
  // Una tarjeta en PCM tiene UNA moneda, pero puede deber en dos. Si el
  // pago va a la línea en dólares de una tarjeta registrada en soles, no
  // hay cambio de moneda que resolver: son dólares contra dólares. Este
  // selector deja decir a qué línea se le está pagando. '' = usar la
  // moneda de la cuenta destino, que es el caso normal.
  const [monedaPago, setMonedaPago] = useState('');
  const [modoCambio, setModoCambio] = useState('monto'); // 'monto' | 'tasa'
  // 21 sep 2026 (Augusto: "cuando se ingrese una transferencia, dar la
  // opción para colocar categoría como cambio de moneda, pago tarjeta, u
  // transferencia entre cuentas").
  //
  // OJO: esto NO es la categoría. La categoría de una transferencia es
  // siempre "Transferencia" -- ese valor es lo que hace que no cuente ni
  // como ingreso ni como gasto en toda la app. Esto es un subtipo aparte
  // (transferencia_tipo). Vacío = que el backend lo deduzca de las
  // cuentas, que acierta en la gran mayoría de los casos.
  const [tipoTransferencia, setTipoTransferencia] = useState('');
  const [tasaCambio, setTasaCambio] = useState('');
  const [transferError, setTransferError] = useState(null);
  // Escape del control de saldo: aparece recién cuando el control se
  // dispara, para no invitar a saltárselo antes de tiempo.
  const [puedeIgnorarSaldo, setPuedeIgnorarSaldo] = useState(false);
  const [ignorarSaldo, setIgnorarSaldo] = useState(false);
  // 21 sep 2026 (Augusto: "el saldo de $2063.16 es el balance al día de
  // hoy, pero no el balance a 01 Julio 2026").
  //
  // Account.saldo es un solo número: el de hoy. Si estás registrando un
  // movimiento de julio, el saldo que importa es el de julio -- y es el
  // que el calendario te muestra. Se pide al backend, que lo reconstruye
  // con el mismo criterio que el calendario, así que los dos coinciden.
  const [saldoEnFecha, setSaldoEnFecha] = useState(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  useEffect(() => {
    let cancelado = false;
    apiClient.get('/dashboard/accounts')
      .then((res) => { if (!cancelado) setCuentas(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelado) setCuentas([]); });
    return () => { cancelado = true; };
  }, []);

  // 4 sep 2026 (Augusto: "la cuenta es de dolares, pero el símbolo sale
  // en soles"): ni el camino puntual (formattedValues) ni el recurrente
  // (buildRecurringPayload) mandaban nunca `moneda` -- el selector
  // "Moneda" que se ve arriba en el form en realidad cambia
  // CurrencyContext (el toggle de VISUALIZACIÓN de toda la app), no un
  // campo de este formulario, así que nunca llegaba al payload. El
  // resultado: toda transacción con Cuenta se guardaba con moneda=null,
  // y TransactionsPage.js cae a "S/" por defecto cuando no hay moneda
  // (ver formatTxAmount) -- aunque la cuenta elegida fuera en dólares.
  // Acá se deriva la moneda real de la Cuenta seleccionada (mismo
  // criterio que ya se usa para la Transferencia, ver
  // guardarTransferencia más abajo).
  // 28 sep 2026 (Augusto: "me dió la opción de usar CAD como moneda,
  // pero tomó el default"; antes: "metí un egreso de CAD$1379... lo pone
  // en moneda local").
  //
  // El selector "Moneda" que se ve arriba en este formulario NO es un
  // campo del formulario: cambia CurrencyContext, el toggle de
  // VISUALIZACIÓN de toda la app (ver el comentario del 4 de septiembre
  // más abajo). Elegir CAD ahí cambiaba cómo se MUESTRAN los montos y el
  // movimiento se guardaba igual con la moneda de la cuenta.
  //
  // Un control llamado "Moneda" dentro de un formulario, que no es un
  // campo de ese formulario, es una trampa. Acá va el campo de verdad.
  const [monedaMovimiento, setMonedaMovimiento] = useState('');
  const [monedasDisponibles, setMonedasDisponibles] = useState([]);
  const [conversionPreview, setConversionPreview] = useState(null);
  const [errorConversion, setErrorConversion] = useState(null);
  useEffect(() => {
    apiClient.get('/transactions/monedas')
      .then((r) => setMonedasDisponibles(r.data || []))
      .catch(() => setMonedasDisponibles([]));
  }, []);

  const monedaDeCuentaSeleccionada = () =>
    accountId === '' ? null : cuentas.find((c) => c.id === Number(accountId))?.moneda || null;

  // Construye el payload para /recurring/incomes o /recurring/expenses —
  // misma lógica que buildRecurringPayload en TransactionsPage.js.
  const buildRecurringPayload = (values) => {
    let fecha_inicio = new Date().toISOString().slice(0, 10);
    if (values.frecuencia === 'mensual') {
      const hoy = new Date();
      const diaElegido = parseInt(values.dia_mes, 10) || hoy.getDate();
      let mes = hoy.getMonth();
      let anio = hoy.getFullYear();
      if (diaElegido < hoy.getDate()) {
        mes += 1;
        if (mes > 11) {
          mes = 0;
          anio += 1;
        }
      }
      const ultimoDiaMes = new Date(anio, mes + 1, 0).getDate();
      const diaFinal = Math.min(diaElegido, ultimoDiaMes);
      fecha_inicio = new Date(anio, mes, diaFinal).toISOString().slice(0, 10);
    }
    return {
      nombre: values.descripcion?.trim() || (values.tipo === 'ingreso' ? 'Ingreso recurrente' : 'Egreso recurrente'),
      monto: parseFloat(values.monto) || 0,
      frecuencia: values.frecuencia,
      categoria: values.categoria || null,
      // 4 sep 2026: antes hardcodeado en null -- ver comentario junto a
      // accountId más arriba.
      account_id: accountId === '' ? null : Number(accountId),
      moneda: monedaMovimiento || monedaDeCuentaSeleccionada(),
      fecha_inicio,
      fecha_fin: null,
    };
  };

  const formik = useFormik({
    initialValues: {
      monto: '',
      descripcion: '',
      categoria: '',
      tipo: 'gasto',
      fecha: new Date(),
      es_recurrente: false,
      frecuencia: 'mensual',
      dia_mes: String(new Date().getDate()),
    },
    validationSchema: getValidationSchema(language),
    onSubmit: async (values) => {
      try {
        if (values.es_recurrente) {
          const payload = buildRecurringPayload(values);
          if (values.tipo === 'ingreso') {
            await dispatch(addIncome(payload)).unwrap();
          } else {
            await dispatch(addExpense(payload)).unwrap();
          }
        } else {
          const formattedValues = {
            tipo: values.tipo,
            monto: parseFloat(values.monto) || 0,
            categoria: values.categoria,
            descripcion: values.descripcion?.trim() || null,
            fecha: values.fecha.toISOString(),
            // 4 sep 2026 (Augusto: "no dice que el dinero ingresó a BBVA
            // Visa"): faltaba mandar la cuenta elegida -- ver comentario
            // junto a accountId más arriba.
            account_id: accountId === '' ? null : Number(accountId),
            // 4 sep 2026 (Augusto: "la cuenta es de dolares, pero el
            // símbolo sale en soles"): ver monedaDeCuentaSeleccionada.
            // 28 sep 2026: si el usuario eligió una moneda para ESTE
            // movimiento, manda esa. El backend convierte a dólares
            // cuando no es soles ni dólares.
            moneda: monedaMovimiento || monedaDeCuentaSeleccionada(),
          };
          const created = await dispatch(addTransaction(formattedValues)).unwrap();
          // 28 ago 2026 (ver app/services/budget_alerts.py en el
          // backend): el diálogo se cierra y la pantalla navega justo
          // después de esto, así que la alerta no puede vivir en el
          // estado local de este form -- se la pasamos al header (que sí
          // sobrevive la navegación) para que la muestre.
          if (created?.alerta_presupuesto && onBudgetAlert) {
            onBudgetAlert(created.alerta_presupuesto);
          }
        }
        if (onClose) onClose();
        navigate(values.es_recurrente ? '/recurring' : '/transactions');
      } catch (error) {
        console.error('Transaction creation failed:', error);
      }
    },
  });

  // 28 sep 2026: equivalente en dólares mientras escribe. Se consulta al
  // backend (que usa la tasa del día de la transacción, no la de hoy).
  const _montoTx = formik.values.monto;
  const _monedaTx = monedaMovimiento || monedaDeCuentaSeleccionada() || 'PEN';
  const _fechaTx = formik.values.fecha;
  useEffect(() => {
    const monto = parseFloat(_montoTx);
    if (!monto || ['PEN', 'USD'].includes(_monedaTx)) {
      setConversionPreview(null);
      setErrorConversion(null);
      return undefined;
    }
    // Espera a que deje de teclear: si no, se consulta la tasa en cada
    // dígito del monto.
    const id = setTimeout(() => {
      apiClient.get('/transactions/conversion', {
        params: {
          monto,
          moneda: _monedaTx,
          fecha: _fechaTx ? new Date(_fechaTx).toISOString().slice(0, 10) : undefined,
        },
      })
        .then((r) => { setConversionPreview(r.data); setErrorConversion(null); })
        .catch((err) => {
          setConversionPreview(null);
          setErrorConversion(
            err.response?.data?.detail ||
              (isSpanish
                ? 'No pude obtener el tipo de cambio. Sin él este monto no se puede guardar.'
                : 'Could not get the exchange rate. This amount cannot be saved without it.')
          );
        });
    }, 500);
    return () => clearTimeout(id);
  }, [_montoTx, _monedaTx, _fechaTx, isSpanish]);


  const fechaSeleccionadaISO = (
    formik.values?.fecha instanceof Date ? formik.values.fecha.toISOString().slice(0, 10) : null
  );
  useEffect(() => {
    if (modo !== 'transferencia' || !cuentaOrigenId || !fechaSeleccionadaISO) {
      setSaldoEnFecha(null);
      return;
    }
    apiClient
      .get(`/dashboard/accounts/${cuentaOrigenId}/saldo-en`, { params: { fecha: fechaSeleccionadaISO } })
      .then((res) => setSaldoEnFecha(res.data))
      // Backend viejo (ventana de deploy) o error de red: se cae al saldo
      // de hoy, que es lo que se mostraba antes.
      .catch(() => setSaldoEnFecha(null));
  }, [modo, cuentaOrigenId, fechaSeleccionadaISO]);

  // 3 sep 2026: camino de guardado de una Transferencia -- aparte del
  // onSubmit de formik de arriba porque una transferencia no tiene
  // categoría ni "tipo" ingreso/gasto (ver comentario en el estado
  // `modo` más arriba), así que el Yup schema del form normal no aplica
  // acá.
  const guardarTransferencia = async () => {
    setTransferError(null);
    const monto = parseFloat(formik.values.monto);
    if (!monto || monto <= 0) {
      setTransferError(isSpanish ? 'El monto debe ser mayor a cero.' : 'Amount must be greater than zero.');
      return;
    }
    if (!cuentaOrigenId || !cuentaDestinoId) {
      setTransferError(isSpanish ? 'Elige la cuenta de origen y la de destino.' : 'Choose the origin and destination accounts.');
      return;
    }
    if (cuentaOrigenId === cuentaDestinoId) {
      setTransferError(isSpanish ? 'La cuenta de origen y la de destino no pueden ser la misma.' : 'Origin and destination accounts cannot be the same.');
      return;
    }
    // 4 sep 2026 (Augusto: "no permita hacer transferencias por un monto
    // superior al saldo en cuentas corrientes o cuentas de ahorros"):
    // chequeo local para no ir y volver del backend -- la validación real
    // (que no se puede saltar) vive en crear_transferencia.
    const cuentaOrigenSel = cuentas.find((c) => c.id === Number(cuentaOrigenId));
    // 21 sep 2026 (Augusto: "no me deja meter la transferencia de tipo de
    // cambio porque dice que no hay saldo, cuando lo hay!").
    //
    // Este control comparaba contra `saldo`, que es el saldo de HOY y
    // además puede estar viejo (se sincroniza con los movimientos desde
    // hace poco; lo importado antes dejó el número congelado). Con eso
    // bloqueaba dos cosas legítimas: registrar un movimiento pasado, y
    // registrar uno real cuando el saldo guardado está desactualizado.
    //
    // Un control que impide anotar un hecho que ya ocurrió no protege
    // nada. Ahora: si la fecha es pasada no se valida, y si es de hoy en
    // adelante avisa pero deja continuar -- la advertencia sigue estando,
    // la decisión es del usuario.
    const fechaTransferencia = formik.values.fecha instanceof Date ? formik.values.fecha : new Date();
    const esPasado = fechaTransferencia < new Date(new Date().toDateString());
    // El saldo que importa es el de la FECHA del movimiento.
    const saldoDeReferencia = (
      saldoEnFecha && saldoEnFecha.account_id === Number(cuentaOrigenId)
        ? saldoEnFecha.saldo_en_fecha
        : (cuentaOrigenSel?.saldo || 0)
    );
    const saldoInsuficiente = !!(
      cuentaOrigenSel
      && ['ahorros', 'corriente'].includes(cuentaOrigenSel.tipo)
      && monto > saldoDeReferencia
    );
    if (!esPasado && saldoInsuficiente && !ignorarSaldo) {
      setTransferError(
        isSpanish
          ? `Según PCM, al ${fechaSeleccionadaISO} tenías ${getCurrencySymbol(cuentaOrigenSel.moneda)} ${Number(saldoDeReferencia).toFixed(2)} en ${cuentaOrigenSel.nombre} y estás transfiriendo ${getCurrencySymbol(cuentaOrigenSel.moneda)} ${monto.toFixed(2)}. Si el movimiento ya ocurrió, ponle su fecha real; si el saldo está desactualizado, registra igual y corrígelo en Cuentas.`
          : `On ${fechaSeleccionadaISO} PCM shows ${getCurrencySymbol(cuentaOrigenSel.moneda)} ${Number(saldoDeReferencia).toFixed(2)} in ${cuentaOrigenSel.nombre} and you are transferring ${getCurrencySymbol(cuentaOrigenSel.moneda)} ${monto.toFixed(2)}. If it already happened, set its real date; if the balance is out of date, record it anyway and fix it in Accounts.`
      );
      setPuedeIgnorarSaldo(true);
      return;
    }
    // 21 sep 2026: si las monedas difieren, hace falta saber cuánto
    // llegó. El backend también lo valida -- esto solo evita el viaje.
    const cuentaDestinoSel = cuentas.find((c) => c.id === Number(cuentaDestinoId));
    const monedaDestinoEfectiva = monedaPago || cuentaDestinoSel?.moneda;
    const hayCambioDeMoneda = !!(
      cuentaOrigenSel?.moneda && monedaDestinoEfectiva &&
      cuentaOrigenSel.moneda !== monedaDestinoEfectiva
    );
    const montoQueLlega = modoCambio === 'tasa'
      ? Number((monto * parseFloat(tasaCambio)).toFixed(2))
      : parseFloat(montoDestino);
    if (hayCambioDeMoneda && (!montoQueLlega || montoQueLlega <= 0)) {
      setTransferError(
        modoCambio === 'tasa'
          ? (isSpanish
              ? 'Indica el tipo de cambio que te aplicaron.'
              : 'Enter the exchange rate you were given.')
          : (isSpanish
              ? `Indica cuánto llegó en ${cuentaDestinoSel.moneda} a ${cuentaDestinoSel.nombre}.`
              : `Enter how much arrived in ${cuentaDestinoSel.moneda} at ${cuentaDestinoSel.nombre}.`)
      );
      return;
    }
    setTransferSubmitting(true);
    try {
      // 4 sep 2026 (Augusto: "la cuenta es de dolares, pero el símbolo
      // sale en soles"): esto mandaba `currency` -- el toggle de moneda
      // de VISUALIZACIÓN de toda la app (PEN/USD, ver CurrencyContext),
      // no la moneda real de la cuenta que se está transfiriendo. Una
      // transferencia entre dos cuentas en dólares, con la app mostrando
      // soles, se guardaba con moneda="PEN" -- el símbolo ($/S/) de cada
      // fila en TransactionsPage.js lee tx.moneda directo (ver
      // formatTxAmount), así que salía "S/" en una cuenta 100% en
      // dólares. Ahora se usa la moneda real de la cuenta de ORIGEN
      // (cuentas viene de /dashboard/accounts, mismo objeto que trae
      // c.moneda) -- currency queda solo de última opción si por algún
      // motivo la cuenta no trajera moneda.
      const cuentaOrigen = cuentas.find((c) => c.id === Number(cuentaOrigenId));
      await dispatch(addTransferencia({
        cuenta_origen_id: Number(cuentaOrigenId),
        cuenta_destino_id: Number(cuentaDestinoId),
        monto,
        monto_destino: hayCambioDeMoneda ? montoQueLlega : null,
        moneda_destino: monedaPago || null,
        transferencia_tipo: tipoTransferencia || null,
        ignorar_saldo: ignorarSaldo,
        moneda: cuentaOrigen?.moneda || currency,
        fecha: formik.values.fecha.toISOString(),
        descripcion: formik.values.descripcion?.trim() || null,
      })).unwrap();
      if (onClose) onClose();
      navigate('/transactions');
    } catch (error) {
      setTransferError(
        error?.detail || (isSpanish ? 'No se pudo crear la transferencia.' : 'Could not create the transfer.')
      );
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (modo === 'transferencia') {
      guardarTransferencia();
    } else {
      formik.handleSubmit(e);
    }
  };

  return (
    <FormPaper>
      {/* Encabezado del formulario */}
      <FormHeader>
        <Typography variant="h6" fontWeight="medium" color="primary">
          {isSpanish ? 'Nueva Transacción' : 'New Transaction'}
        </Typography>
        {onClose && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </FormHeader>

      {/* Contenido del formulario */}
      <FormContent>
        <form onSubmit={handleFormSubmit}>
          {/* Tipo de transacción: Ingreso, Egreso o Transferencia */}
          <FieldWrapper>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {isSpanish ? 'Tipo de Transacción' : 'Transaction Type'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <TypeButton
                type="button"
                buttonType="income"
                active={modo === 'ingreso'}
                onClick={() => { setModo('ingreso'); formik.setFieldValue('tipo', 'ingreso'); }}
                startIcon={<ArrowUpwardIcon />}
              >
                {isSpanish ? 'Ingreso' : 'Income'}
              </TypeButton>
              <TypeButton
                type="button"
                buttonType="expense"
                active={modo === 'gasto'}
                onClick={() => { setModo('gasto'); formik.setFieldValue('tipo', 'gasto'); }}
                startIcon={<ArrowDownwardIcon />}
              >
                {isSpanish ? 'Egreso' : 'Expense'}
              </TypeButton>
              {/* 3 sep 2026 (Augusto: "no existe la opcion para transferir
                  de cuenta a cuenta o a tarjetas, para mostrar pagos de
                  tarjeta"): tercer modo -- ver guardarTransferencia y el
                  bloque de Cuenta origen/destino más abajo. */}
              <TypeButton
                type="button"
                buttonType="transfer"
                active={modo === 'transferencia'}
                onClick={() => setModo('transferencia')}
                startIcon={<SwapHorizIcon />}
              >
                {isSpanish ? 'Transferencia' : 'Transfer'}
              </TypeButton>
            </Box>
          </FieldWrapper>

          <Divider sx={{ my: 2 }} />

          {/* Monto y Descripción */}
          {/* 28 sep 2026 (Augusto: "salen dos monedas para seleccionar en
              el formulario de Nueva Transacción").

              Acá había un selector "Moneda" que NO era un campo de este
              formulario: cambiaba CurrencyContext, el toggle de
              VISUALIZACIÓN de toda la app. Elegir CAD ahí no hacía que el
              movimiento se guardara en dólares canadienses -- solo
              cambiaba el símbolo que se ve en el resto de las pantallas,
              y el gasto se guardaba con la moneda de la cuenta. Fue
              exactamente así como CAD 1,379 terminó anotado como S/ 1,379.

              Se elimina de este formulario. El toggle de visualización
              sigue existiendo donde corresponde (el encabezado de la app),
              y acá queda un único campo de moneda, pegado al monto, que sí
              decide en qué moneda se guarda el movimiento. */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FieldWrapper>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {isSpanish ? 'Monto' : 'Amount'}
                </Typography>
                {/* 28 sep 2026 (Augusto: "me dió la opción de usar CAD
                    como moneda, pero tomó el default"). Este SÍ es un
                    campo del formulario, a diferencia del selector
                    "Moneda" de arriba, que solo cambia cómo se muestran
                    los montos en toda la app. Va pegado al monto porque
                    es parte del monto: "1379" no significa nada sin él. */}
                {monedasDisponibles.length > 0 && (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                    label={isSpanish ? 'Moneda de este movimiento' : 'Currency of this transaction'}
                    value={monedaMovimiento || monedaDeCuentaSeleccionada() || 'PEN'}
                    onChange={(e) => setMonedaMovimiento(e.target.value)}
                    SelectProps={{ native: false }}
                  >
                    {monedasDisponibles.map((m) => (
                      <MenuItem key={m.codigo} value={m.codigo}>
                        {m.codigo} — {m.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {conversionPreview && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {isSpanish
                      ? `Se guardará como US$ ${conversionPreview.monto_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })} (tasa ${conversionPreview.tasa}). Es un estimado con la tasa del día; tu banco suma su comisión. Al importar el extracto, PCM lo reemplaza por la cifra real.`
                      : `Will be saved as US$ ${conversionPreview.monto_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })} (rate ${conversionPreview.tasa}). Estimate at today's rate; your bank adds its FX fee. Importing the statement replaces it with the real figure.`}
                  </Alert>
                )}
                {errorConversion && (
                  <Alert severity="warning" sx={{ mb: 1 }}>{errorConversion}</Alert>
                )}
                <TextField
                  fullWidth
                  id="monto"
                  name="monto"
                  type="number"
                  placeholder="0.00"
                  variant="outlined"
                  value={formik.values.monto}
                  onChange={formik.handleChange}
                  error={formik.touched.monto && Boolean(formik.errors.monto)}
                  helperText={formik.touched.monto && formik.errors.monto}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body1" sx={{ color: goldColors.main, fontWeight: 'bold' }}>
                          {getCurrencySymbol(currency)}
                        </Typography>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme => theme.palette.divider
                      },
                    }
                  }}
                />
              </FieldWrapper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldWrapper>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {isSpanish ? 'Fecha' : 'Date'}
                </Typography>
                <TextField
                  fullWidth
                  id="fecha"
                  name="fecha"
                  type="date"
                  variant="outlined"
                  value={formik.values.fecha instanceof Date ? formik.values.fecha.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const date = new Date(e.target.value);
                    formik.setFieldValue('fecha', date);
                  }}
                  error={formik.touched.fecha && Boolean(formik.errors.fecha)}
                  helperText={formik.touched.fecha && formik.errors.fecha}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ color: goldColors.main }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme => theme.palette.divider
                      },
                    }
                  }}
                />
              </FieldWrapper>
            </Grid>
          </Grid>

          <FieldWrapper sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {isSpanish ? 'Descripción' : 'Description'}
            </Typography>
            <TextField
              fullWidth
              id="descripcion"
              name="descripcion"
              placeholder={isSpanish ? 'Escribe una descripción breve...' : 'Write a brief description...'}
              variant="outlined"
              value={formik.values.descripcion}
              onChange={formik.handleChange}
              error={formik.touched.descripcion && Boolean(formik.errors.descripcion)}
              helperText={formik.touched.descripcion && formik.errors.descripcion}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon sx={{ color: goldColors.main }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme => theme.palette.divider
                  },
                }
              }}
            />
          </FieldWrapper>

          <Divider sx={{ my: 2 }} />

          {/* Categoría -- no aplica a una Transferencia (ver modo ===
              'transferencia' más abajo: ahí se pide Cuenta origen/destino
              en su lugar). */}
          {modo !== 'transferencia' && (
            <FieldWrapper>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isSpanish ? 'Categoría' : 'Category'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                {CATEGORIAS.map((cat) => (
                  <CategoryChip
                    key={cat}
                    label={categoriaLabel(cat, isSpanish)}
                    selected={formik.values.categoria === cat}
                    onClick={() => formik.setFieldValue('categoria', cat)}
                  />
                ))}
              </Box>
              {formik.touched.categoria && formik.errors.categoria && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {formik.errors.categoria}
                </Typography>
              )}
            </FieldWrapper>
          )}

          {/* 4 sep 2026 (Augusto: "no dice que el dinero ingresó a BBVA
              Visa"): este selector no existía -- un Ingreso/Gasto creado
              desde este diálogo (el "+" del header) nunca quedaba ligado a
              ninguna cuenta, sin ningún aviso. "Sin asociar" (igual que en
              Registros → Transacciones → Agregar) deja crear un movimiento
              suelto a propósito, para quien de verdad no quiere ligarlo a
              una cuenta. */}
          {modo !== 'transferencia' && (
            <FieldWrapper>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isSpanish ? 'Cuenta (opcional)' : 'Account (optional)'}
              </Typography>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="">{isSpanish ? 'Sin asociar' : 'Not linked'}</MenuItem>
                  {cuentas.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nombre}{c.entidad ? ` (${c.entidad})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FieldWrapper>
          )}

          {/* 3 sep 2026 (Augusto: "no existe la opcion para transferir de
              cuenta a cuenta o a tarjetas, para mostrar pagos de
              tarjeta"): una tarjeta de crédito ya ES una Account más
              (Account.tipo="tarjeta_credito", ver CuentasPage.js), así
              que este mismo selector sirve tanto para transferir entre
              cuentas bancarias como para pagar una tarjeta -- no hace
              falta nada especial para "tarjeta". Lista viene de
              GET /dashboard/accounts (mismo endpoint que usa Calendario). */}
          {modo === 'transferencia' && (
            <FieldWrapper>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isSpanish ? 'Cuenta de origen' : 'Origin account'}
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  displayEmpty
                  value={cuentaOrigenId}
                  onChange={(e) => setCuentaOrigenId(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="" disabled>
                    {isSpanish ? 'Elige la cuenta de origen' : 'Choose the origin account'}
                  </MenuItem>
                  {cuentas.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nombre}{c.entidad ? ` (${c.entidad})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* 4 sep 2026 (Augusto: "no permita hacer transferencias por
                  un monto superior al saldo en cuentas corrientes o
                  cuentas de ahorros"): aviso del saldo disponible apenas
                  se elige la cuenta de origen -- el backend igual lo
                  valida (ver crear_transferencia), esto es solo para que
                  el cliente lo vea antes de intentar guardar. No aplica a
                  tarjetas de crédito ni otra deuda (no tienen un "saldo
                  disponible" real, ver TIPOS_ACTIVO en CuentasPage.js). */}
              {(() => {
                const origen = cuentas.find((c) => c.id === Number(cuentaOrigenId));
                if (!origen || !['ahorros', 'corriente'].includes(origen.tipo)) return null;
                return (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: -1 }}>
                    {/* 21 sep 2026 (Augusto: "inconsistencia en los
                        balances para Julio 1"). Decía "Saldo disponible"
                        a secas, y eso se lee como "el saldo", en general.
                        Es el de HOY: el del calendario en una fecha
                        pasada es otro número y tiene que serlo. Decirlo
                        acá evita la contradicción aparente. */}
                    {/* 21 sep 2026 (Augusto: "el saldo de $2063.16 es el
                        balance al día de hoy, pero no el balance a 01
                        Julio 2026"). Se muestra el saldo de la FECHA que
                        elegiste, reconstruido igual que en el calendario
                        -- por eso los dos números coinciden. Si el
                        backend no responde, se cae al de hoy y lo dice. */}
                    {(() => {
                      const aLaFecha = saldoEnFecha && saldoEnFecha.account_id === Number(cuentaOrigenId);
                      const valor = aLaFecha ? saldoEnFecha.saldo_en_fecha : Number(origen.saldo || 0);
                      const cuando = aLaFecha
                        ? saldoEnFecha.fecha
                        : (isSpanish ? 'hoy' : 'today');
                      return (
                        <>
                          {isSpanish ? `Saldo al ${cuando}: ` : `Balance on ${cuando}: `}
                          {getCurrencySymbol(origen.moneda)} {Number(valor).toFixed(2)}
                          {aLaFecha && saldoEnFecha.movimientos_posteriores > 0 && (
                            isSpanish
                              ? ` · Hoy son ${getCurrencySymbol(origen.moneda)} ${Number(saldoEnFecha.saldo_hoy).toFixed(2)}, después de ${saldoEnFecha.movimientos_posteriores} movimiento(s) posteriores.`
                              : ` · Today it is ${getCurrencySymbol(origen.moneda)} ${Number(saldoEnFecha.saldo_hoy).toFixed(2)}, after ${saldoEnFecha.movimientos_posteriores} later transaction(s).`
                          )}
                        </>
                      );
                    })()}
                  </Typography>
                );
              })()}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isSpanish ? 'Cuenta destino (o tarjeta a pagar)' : 'Destination account (or card to pay)'}
              </Typography>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={cuentaDestinoId}
                  onChange={(e) => setCuentaDestinoId(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="" disabled>
                    {isSpanish ? 'Elige la cuenta destino' : 'Choose the destination account'}
                  </MenuItem>
                  {cuentas.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nombre}{c.entidad ? ` (${c.entidad})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {cuentas.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {isSpanish
                    ? 'Todavía no tienes cuentas registradas -- créalas primero en Cuentas.'
                    : 'You don\'t have any accounts yet -- create them first under Accounts.'}
                </Typography>
              )}
              {/* 21 sep 2026 (Augusto: "PCM no tiene una opción de
                  transferencia de cuenta USD a local"): cuando las dos
                  cuentas están en monedas distintas, sale un segundo
                  campo. Antes esto no existía y los dos lados se
                  guardaban con el mismo número -- cambiar US$ 2,731
                  registraba "S/ 2,731" entrando a la cuenta en soles. */}
              {(() => {
                const origen = cuentas.find((c) => c.id === Number(cuentaOrigenId));
                const destino = cuentas.find((c) => c.id === Number(cuentaDestinoId));
                if (!origen?.moneda || !destino?.moneda) return null;
                // 21 sep 2026: una tarjeta puede deber en dos monedas a la
                // vez (PCM guarda una deuda por moneda). Por eso, cuando el
                // destino es tarjeta, se pregunta a qué línea va el pago en
                // vez de asumirlo de la moneda de la cuenta.
                const destinoEsTarjeta = destino.tipo === 'tarjeta_credito';
                const monedaDestino = monedaPago || destino.moneda;
                const selectorMoneda = destinoEsTarjeta ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {isSpanish ? '¿En qué moneda es el pago?' : 'Which currency is the payment in?'}
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={monedaDestino}
                      onChange={(e, v) => { if (v) setMonedaPago(v); }}
                    >
                      <ToggleButton value="PEN">PEN</ToggleButton>
                      <ToggleButton value="USD">USD</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {isSpanish
                        ? 'Una tarjeta puede deber en soles y en dólares a la vez. Elige a cuál de las dos le estás pagando.'
                        : 'A card can owe in both currencies. Choose which balance you are paying.'}
                    </Typography>
                  </Box>
                ) : null;
                // Mismas monedas: no hay nada que convertir. Solo se
                // muestra el selector, si aplica.
                if (origen.moneda === monedaDestino) return selectorMoneda;
                const sale = parseFloat(formik.values.monto);
                // Las dos caras del mismo dato: se completa una y se
                // muestra la otra calculada, para poder verificar contra
                // el voucher sin sacar la calculadora.
                const llega = modoCambio === 'tasa'
                  ? (sale > 0 && parseFloat(tasaCambio) > 0 ? sale * parseFloat(tasaCambio) : null)
                  : (parseFloat(montoDestino) > 0 ? parseFloat(montoDestino) : null);
                const tasa = modoCambio === 'tasa'
                  ? (parseFloat(tasaCambio) > 0 ? parseFloat(tasaCambio) : null)
                  : (sale > 0 && llega ? llega / sale : null);
                return (
                  <Box sx={{ mt: 2 }}>
                    {selectorMoneda}
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: selectorMoneda ? 2 : 0 }}>
                      {isSpanish
                        ? `Cambio de ${origen.moneda} a ${monedaDestino}`
                        : `${origen.moneda} to ${monedaDestino} exchange`}
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={modoCambio}
                      onChange={(e, v) => { if (v) setModoCambio(v); }}
                      sx={{ mb: 1.5 }}
                    >
                      <ToggleButton value="monto">
                        {isSpanish ? 'Sé cuánto llegó' : 'I know the amount'}
                      </ToggleButton>
                      <ToggleButton value="tasa">
                        {isSpanish ? 'Sé el tipo de cambio' : 'I know the rate'}
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {modoCambio === 'monto' ? (
                      <TextField
                        fullWidth
                        type="number"
                        label={isSpanish ? `Monto que llegó a ${destino.nombre}` : `Amount received at ${destino.nombre}`}
                        value={montoDestino}
                        onChange={(e) => setMontoDestino(e.target.value)}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {getCurrencySymbol(monedaDestino)}
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        type="number"
                        label={isSpanish
                          ? `Tipo de cambio (1 ${origen.moneda} = ? ${monedaDestino})`
                          : `Exchange rate (1 ${origen.moneda} = ? ${monedaDestino})`}
                        value={tasaCambio}
                        onChange={(e) => setTasaCambio(e.target.value)}
                        placeholder="3.8100"
                        inputProps={{ step: '0.0001' }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                    )}

                    {/* El dato que NO se escribió, calculado. Es la forma
                        de darse cuenta de un error de tipeo antes de
                        guardar: una tasa de 38 en vez de 3.8 salta a la
                        vista cuando se ve el monto resultante. */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {modoCambio === 'tasa'
                        ? (llega
                            ? (isSpanish
                                ? `Llegan ${getCurrencySymbol(monedaDestino)} ${llega.toFixed(2)} a ${destino.nombre}.`
                                : `${getCurrencySymbol(monedaDestino)} ${llega.toFixed(2)} arrive at ${destino.nombre}.`)
                            : (isSpanish
                                ? 'Pon la tasa que te aplicaron y acá verás cuánto llega.'
                                : 'Enter the rate you were given and the amount will show here.'))
                        : (tasa
                            ? (isSpanish
                                ? `Tipo de cambio: ${tasa.toFixed(4)}.`
                                : `Exchange rate: ${tasa.toFixed(4)}.`)
                            : (isSpanish
                                ? 'Pon el monto exacto que te depositó el banco.'
                                : 'Enter the exact amount the bank deposited.'))}
                    </Typography>
                  </Box>
                );
              })()}
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel id="tipo-transferencia-label">
                  {isSpanish ? '¿Qué tipo de transferencia es?' : 'What kind of transfer is this?'}
                </InputLabel>
                <Select
                  labelId="tipo-transferencia-label"
                  label={isSpanish ? '¿Qué tipo de transferencia es?' : 'What kind of transfer is this?'}
                  value={tipoTransferencia}
                  onChange={(e) => setTipoTransferencia(e.target.value)}
                >
                  <MenuItem value="">
                    {isSpanish ? 'Detectar automáticamente' : 'Detect automatically'}
                  </MenuItem>
                  <MenuItem value="entre_cuentas">
                    {isSpanish ? 'Entre mis cuentas' : 'Between my accounts'}
                  </MenuItem>
                  <MenuItem value="pago_tarjeta">
                    {isSpanish ? 'Pago de tarjeta' : 'Card payment'}
                  </MenuItem>
                  <MenuItem value="cambio_moneda">
                    {isSpanish ? 'Cambio de moneda' : 'Currency exchange'}
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {isSpanish
                    ? 'Sirve para leer tus movimientos; en ningún caso cuenta como ingreso ni como gasto.'
                    : 'This is only for reading your records; a transfer never counts as income or spending.'}
                </FormHelperText>
              </FormControl>
              {transferError && (
                <Alert
                  severity={puedeIgnorarSaldo ? 'warning' : 'error'}
                  sx={{ mt: 2 }}
                  action={puedeIgnorarSaldo ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => { setIgnorarSaldo(true); setTransferError(null); setPuedeIgnorarSaldo(false); }}
                    >
                      {isSpanish ? 'Registrar igual' : 'Record anyway'}
                    </Button>
                  ) : null}
                >
                  {transferError}
                </Alert>
              )}
            </FieldWrapper>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Recurrente (31 jul 2026): si se marca, esto no crea una
              Transaction sino una serie en Income/Expense (mismo backend
              que ya usa la pantalla "Recurrentes"), visible ahí y
              proyectada en el Calendario de flujo de efectivo. No aplica
              a una Transferencia (3 sep 2026). */}
          {modo !== 'transferencia' && (
          <FieldWrapper>
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.es_recurrente}
                  onChange={(e) => formik.setFieldValue('es_recurrente', e.target.checked)}
                />
              }
              label={isSpanish ? '¿Es recurrente?' : 'Is recurring?'}
            />
            {formik.values.es_recurrente && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {isSpanish
                    ? 'Se guardará como una serie programada (visible en "Recurrentes" y proyectada en el Calendario) en vez de un movimiento puntual.'
                    : 'This will be saved as a scheduled series (visible under "Recurring" and projected on the Calendar) instead of a one-time movement.'}
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="frecuencia-form-label">{isSpanish ? 'Frecuencia' : 'Frequency'}</InputLabel>
                  <Select
                    labelId="frecuencia-form-label"
                    name="frecuencia"
                    value={formik.values.frecuencia}
                    label={isSpanish ? 'Frecuencia' : 'Frequency'}
                    onChange={formik.handleChange}
                  >
                    {frecuenciasRecurrente.map((f) => (
                      <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {formik.values.frecuencia === 'mensual' && (
                  <TextField
                    margin="dense"
                    name="dia_mes"
                    label={isSpanish ? 'Día del mes (1-31)' : 'Day of month (1-31)'}
                    type="number"
                    fullWidth
                    inputProps={{ min: 1, max: 31 }}
                    value={formik.values.dia_mes}
                    onChange={formik.handleChange}
                    helperText={isSpanish ? 'El día en que se recibe el ingreso o se paga el egreso cada mes.' : undefined}
                  />
                )}
              </Box>
            )}
          </FieldWrapper>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Botones de acción */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            {onClose && (
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ borderRadius: '8px' }}
              >
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={modo === 'transferencia' ? transferSubmitting : (!formik.isValid || formik.isSubmitting)}
              sx={{
                borderRadius: '8px',
                bgcolor: goldColors.main,
                '&:hover': {
                  bgcolor: goldColors.dark,
                }
              }}
            >
              {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </Box>
        </form>
      </FormContent>
    </FormPaper>
  );
};

export default TransactionForm;
